import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Industry, Role, QuestionSet, Question, UploadedDocumentSummary } from '../../../../types';
import { IndustrySelector } from '../../../forms/IndustrySelector';
import { RoleSelector } from '../../../forms/RoleSelector';
import { DocumentUpload } from '../../../forms/DocumentUpload';
import { ProgressIndicator } from '../../../ui/ProgressIndicator';
import { SaveIndicator } from '../../../ui/SaveIndicator';
import { AIQuestions } from '../AIQuestions';
import { AdditionalQuestions } from '../AdditionalQuestions';
import { useTheme } from '../../../../context/ThemeContext';
import { apiService } from '../../../../services';
import { useAutoSave } from '../../../../hooks/useAutoSave';
// Progress management handled by useAutoSave hook
import { useAuth } from '../../../../context/AuthContext';
import axios from 'axios';
import styles from './AssessmentSetup.module.css';
import { getApiUrl } from '../../../../config/environments';

interface AssessmentSetupProps {
  onSetupComplete: (industry: Industry, role: Role, questionSet: QuestionSet) => void;
  shouldLoadProgress?: boolean;
}

type SetupStep = 'info' | 'overview' | 'additional-questions' | 'document-upload';

const AssessmentSetup: React.FC<AssessmentSetupProps> = ({ 
  onSetupComplete,
  shouldLoadProgress = false 
}) => {
  const navigate = useNavigate();
  const { setIndustryTheme } = useTheme();
  const [currentStep, setCurrentStep] = useState<SetupStep>('info');
  const [companyName, setCompanyName] = useState<string>('');
  const [selectedIndustry, setSelectedIndustry] = useState<Industry | null>(null);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [questionSet, setQuestionSet] = useState<QuestionSet | null>(null);
  const [aiQuestions, setAiQuestions] = useState<Question[]>([]);
  const [aiAnswers, setAiAnswers] = useState<Record<string, string>>({});
  const [additionalQuestions, setAdditionalQuestions] = useState<Question[]>([]);
  const [additionalAnswers, setAdditionalAnswers] = useState<Record<string, string>>({});
  const [showAdditionalQuestions, setShowAdditionalQuestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showStartNewConfirm, setShowStartNewConfirm] = useState(false);
  const [uploadedDocuments, setUploadedDocuments] = useState<UploadedDocumentSummary[]>([]);
  const [documentUploadError, setDocumentUploadError] = useState<string | null>(null);
  const [assessmentId, setAssessmentId] = useState<string | null>(null);
  const { isSignedIn, getToken } = useAuth();
  const fetchingAssessmentIdRef = React.useRef(false);

  const shouldShowAdditionalStep = showAdditionalQuestions || additionalQuestions.length > 0;

  // Initialize auto-save hook with 500ms debounce for text inputs
  const { progress, saveStatus, updateProgress, saveNow, clearAllProgress } = useAutoSave({
    debounceMs: 500, // Reduced from 2000ms to 500ms for faster saves
    onSaveSuccess: () => {
      console.log('✓ Progress saved successfully');
    },
    onSaveError: (error) => {
      console.error('❌ Error saving progress:', error);
    },
  });

  // Determine if overview step is completed (when user has moved past it)
  const overviewCompleted = currentStep === 'additional-questions' ||
                            currentStep === 'document-upload' ||
                            (questionSet !== null && aiQuestions.length > 0);

  /**
   * Detecta si el usuario tiene progreso sin guardar
   *
   * Retorna true si:
   * - Hay nombre de empresa escrito
   * - Hay industria seleccionada
   * - Hay respuestas en las preguntas
   * - Hay documentos subidos
   *
   * Esto se usa para mostrar confirmación al salir
   */
  const hasUnsavedProgress = Boolean(
    companyName.trim() ||
    selectedIndustry ||
    Object.keys(aiAnswers).length > 0 ||
    Object.keys(additionalAnswers).length > 0 ||
    uploadedDocuments.length > 0
  );

  /**
   * Efecto para mostrar confirmación al cerrar/recargar la página
   *
   * El evento 'beforeunload' se dispara cuando:
   * - El usuario cierra la pestaña/navegador
   * - El usuario recarga la página (F5)
   * - El usuario navega a otra URL externa
   *
   * Nota: Los navegadores modernos ignoran el mensaje personalizado
   * y muestran su propio mensaje genérico por seguridad
   */
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      // Solo mostrar confirmación si hay progreso
      if (hasUnsavedProgress) {
        // Mensaje estándar (los navegadores modernos lo ignoran)
        const message = 'Tienes progreso sin guardar. ¿Estás seguro de que quieres salir?';

        // Forma estándar de prevenir la salida
        event.preventDefault();

        // Para navegadores antiguos (Chrome < 119, etc.)
        event.returnValue = message;

        return message;
      }
    };

    // Agregar el listener
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup: remover el listener cuando el componente se desmonte
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedProgress]); // Se re-ejecuta cuando cambia hasUnsavedProgress

  const steps = [
    {
      id: 'info',
      title: 'Información',
      description: 'Empresa, industria y rol',
      completed: companyName !== '' && selectedIndustry !== null && selectedRole !== null,
      current: currentStep === 'info'
    },
    {
      id: 'overview',
      title: 'Preguntas Iniciales',
      description: 'Contexto general',
      completed: overviewCompleted,
      current: currentStep === 'overview'
    },
    {
      id: 'additional-questions',
      title: 'Preguntas Detalladas',
      description: 'Información específica',
      completed: additionalQuestions.length > 0,
      current: currentStep === 'additional-questions'
    },
    {
      id: 'document-upload',
      title: 'Documentos',
      description: 'Archivos opcionales',
      completed: uploadedDocuments.length > 0,
      current: currentStep === 'document-upload'
    }
  ];

  // Fetch assessment ID from backend when needed
  useEffect(() => {
    let isMounted = true;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const fetchAssessmentId = async () => {
      // Don't fetch if we already have an ID, user is not signed in, or already fetching
      if (assessmentId || !isSignedIn || fetchingAssessmentIdRef.current) {
        return;
      }

      // Fetch ID when we have industry and role selected (after step 1)
      // or when we're on document-upload step
      const shouldFetch = (selectedIndustry && selectedRole) || currentStep === 'document-upload';
      if (!shouldFetch) {
        return;
      }

      // Mark as fetching to prevent concurrent calls
      fetchingAssessmentIdRef.current = true;

      try {
        const token = await getToken();
        if (!token || !isMounted) return;
        
        // Try to get existing assessment
        const response = await axios.get(`${getApiUrl()}/assessments/progress`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        console.log('🔍 Assessment progress response:', {
          hasData: !!response.data,
          dataKeys: response.data ? Object.keys(response.data) : [],
          id: response.data?.id,
          _id: response.data?._id,
          fullData: response.data
        });

        // If assessment exists, use its ID (check both id and _id)
        if (isMounted && response.data) {
          const assessmentIdValue = response.data.id || response.data._id;
          if (assessmentIdValue) {
            setAssessmentId(assessmentIdValue);
            console.log('✅ Assessment ID fetched:', assessmentIdValue);
            fetchingAssessmentIdRef.current = false;
            return;
          } else {
            console.warn('⚠️ Assessment data exists but no ID found:', response.data);
          }
        }

        // If no assessment exists, force a sync to create one (only once)
        if (isMounted && !response.data) {
          console.log('📝 No assessment found, forcing sync to create one...');
          await saveNow(); // Force immediate save which will sync to backend
          
          // Wait a bit for sync to complete, then fetch again (only once)
          timeoutId = setTimeout(async () => {
            if (!isMounted) {
              fetchingAssessmentIdRef.current = false;
              return;
            }
            try {
              const retryResponse = await axios.get(`${getApiUrl()}/assessments/progress`, {
                headers: {
                  Authorization: `Bearer ${token}`
                }
              });
              
              if (isMounted && retryResponse.data) {
                const assessmentIdValue = retryResponse.data.id || retryResponse.data._id;
                if (assessmentIdValue) {
                  setAssessmentId(assessmentIdValue);
                  console.log('✅ Assessment ID created and fetched:', assessmentIdValue);
                } else {
                  console.warn('⚠️ Assessment created but no ID found:', retryResponse.data);
                }
              }
            } catch (err: any) {
              if (isMounted) {
                console.error('Error fetching assessment ID after sync:', err);
              }
            } finally {
              fetchingAssessmentIdRef.current = false;
            }
          }, 1000);
          return; // Exit early, will reset flag in timeout
        }
        
      } catch (err: any) {
        if (isMounted) {
          console.error('Error fetching assessment ID:', err);
        }
        fetchingAssessmentIdRef.current = false;
      }
    };

    fetchAssessmentId();

    return () => {
      isMounted = false;
      fetchingAssessmentIdRef.current = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [currentStep, selectedIndustry?.id, selectedRole?.id, assessmentId, isSignedIn]); // Only depend on IDs, not full objects

  // Load saved progress on mount
  useEffect(() => {
    if (shouldLoadProgress && progress.data.step1.industry) {
      console.log('Restoring saved progress...');
      
      // Restore step 1
      if (progress.data.step1.companyName) {
        setCompanyName(progress.data.step1.companyName);
      }
      if (progress.data.step1.industry) {
        setSelectedIndustry(progress.data.step1.industry);
        setIndustryTheme(progress.data.step1.industry);
      }
      
      // Restore step 2
      if (progress.data.step2.role) {
        setSelectedRole(progress.data.step2.role);
      }
      
      // Restore step 3
      if (progress.data.step3.questionSet) {
        setQuestionSet(progress.data.step3.questionSet);
        setAiQuestions(progress.data.step3.aiQuestions);
        setAiAnswers(progress.data.step3.aiAnswers);
      }
      
      // Restore step 4
      if (progress.data.step4.additionalQuestions.length > 0) {
        setAdditionalQuestions(progress.data.step4.additionalQuestions);
        setAdditionalAnswers(progress.data.step4.additionalAnswers);
        setShowAdditionalQuestions(true);
      }

      // Restore step 5
      if (progress.data.step5.documents.length > 0) {
        setUploadedDocuments(progress.data.step5.documents);
      }
      
      // Restore current step
      setCurrentStep(progress.currentStep);
      
      console.log('Progress restored:', progress);
    }
  }, [shouldLoadProgress]);

  const handleCompanyNameChange = (name: string) => {
    setCompanyName(name);
    
    // Debounced save for company name
    updateProgress((prev) => ({
      ...prev,
      data: {
        ...prev.data,
        step1: {
          ...prev.data.step1,
          companyName: name,
        },
      },
    }));
  };

  const handleIndustrySelect = (industry: Industry) => {
    setSelectedIndustry(industry);
    setSelectedRole(null); // Reset role when industry changes
    setQuestionSet(null);
    setError(null);
    // Apply industry theme
    setIndustryTheme(industry);
    
    // 🚀 IMMEDIATE SAVE - Step 1 (Industry Selection)
    // Saves instantly (0ms delay) when user clicks an industry
    updateProgress((prev) => ({
      ...prev,
      data: {
        ...prev.data,
        step1: {
          ...prev.data.step1,
          industry,
          completedAt: new Date().toISOString(),
        },
      },
    }), { immediate: true }); // ← IMMEDIATE SAVE
  };

  const handleRoleSelect = async (role: Role) => {
    setSelectedRole(role);
    setError(null);
    
    // 🔥 CRITICAL: Check if questions already exist in localStorage
    // This prevents regenerating questions when navigating back to this step
    const savedQuestions = progress?.data?.step3?.aiQuestions;
    const savedAnswers = progress?.data?.step3?.aiAnswers || {};
    const savedQuestionSet = progress?.data?.step3?.questionSet;
    
    // If we already have questions for this role, restore them instead of calling API
    if (savedQuestions && savedQuestions.length > 0 && savedQuestionSet) {
      console.log('📦 Restoring questions from localStorage (no API call)');
      setQuestionSet(savedQuestionSet);
      setAiQuestions(savedQuestions);
      setAiAnswers(savedAnswers);
      setCurrentStep('overview');
      
      // Save the role selection (questions already saved)
      updateProgress((prev) => ({
        ...prev,
        currentStep: 'overview',
        data: {
          ...prev.data,
          step1: {
            ...prev.data.step1,
            role,
          },
          step2: {
            role,
            completedAt: new Date().toISOString(),
          },
        },
      }), { immediate: true });
      
      return; // Don't call API
    }
    
    // 🤖 First time: Generate questions with AI
    console.log('🤖 Generating questions with AI (first time)');
    try {
      setLoading(true);
      const questions = await apiService.getQuestionsWithAI(
        selectedIndustry!.id,
        role.id
      );
      setQuestionSet(questions);
      
      // Extract AI questions from the question set
      const aiQuestions = questions.questions.filter((q: Question) => q.isAiGenerated);
      setAiQuestions(aiQuestions);
      
      setCurrentStep('overview');
      
      // 🚀 IMMEDIATE SAVE - Step 2 & 3 (Role Selection + Questions Loaded)
      // Saves instantly when role is selected and questions are loaded
      updateProgress((prev) => ({
        ...prev,
        currentStep: 'overview',
        data: {
          ...prev.data,
          step2: {
            role,
            completedAt: new Date().toISOString(),
          },
          step3: {
            questionSet: questions,
            aiQuestions,
            aiAnswers: {},
            reviewedAt: new Date().toISOString(),
          },
        },
      }), { immediate: true }); // ← IMMEDIATE SAVE
    } catch (err) {
      setError('Failed to load questions. Please try again.');
      console.error('Error loading questions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (currentStep === 'overview') {
      setCurrentStep('info');
      setQuestionSet(null);
    } else if (currentStep === 'additional-questions') {
      setCurrentStep('overview');
    } else if (currentStep === 'document-upload') {
      if (shouldShowAdditionalStep) {
        setCurrentStep('additional-questions');
      } else {
        setCurrentStep('overview');
      }
    }
    setError(null);
  };

  const handleAIAnswersChange = useCallback((answers: Record<string, string>) => {
    setAiAnswers(answers);
    
    // Check if all AI questions are answered
    const allAnswered = aiQuestions.every(q => answers[q.id] && answers[q.id].trim().length > 0);
    if (allAnswered && !showAdditionalQuestions) {
      setShowAdditionalQuestions(true);
    }
    
    // ⏱️ DEBOUNCED SAVE (500ms) - Step 3 (AI Answers)
    // Saves 500ms after user stops typing in text inputs
    updateProgress((prev) => ({
      ...prev,
      data: {
        ...prev.data,
        step3: {
          ...prev.data.step3,
          aiAnswers: answers,
        },
      },
    })); // No { immediate: true } = uses 500ms debounce
  }, [aiQuestions, showAdditionalQuestions, updateProgress]);

  // Check if all AI questions are answered for button state
  const allAIQuestionsAnswered = aiQuestions.length > 0 && aiQuestions.every(q => aiAnswers[q.id] && aiAnswers[q.id].trim().length > 0);

  const handleAdditionalQuestionsGenerated = useCallback((questions: Question[]) => {
    setAdditionalQuestions(questions);
    
    // 🚀 IMMEDIATE SAVE - Step 4 (Additional Questions Generated)
    // Saves instantly when AI generates additional questions
    updateProgress((prev) => ({
      ...prev,
      data: {
        ...prev.data,
        step4: {
          ...prev.data.step4,
          additionalQuestions: questions,
          progress: {
            answered: 0,
            total: questions.length,
          },
        },
      },
    }), { immediate: true }); // ← IMMEDIATE SAVE
  }, [updateProgress]);

  const handleAdditionalAnswersChange = useCallback((answers: Record<string, string>) => {
    setAdditionalAnswers(answers);
    
    // Calculate progress
    const answeredCount = Object.values(answers).filter(a => a.trim().length > 0).length;
    
    // ⏱️ DEBOUNCED SAVE (500ms) - Step 4 (Additional Answers)
    // Saves 500ms after user stops typing in text inputs
    updateProgress((prev) => ({
      ...prev,
      data: {
        ...prev.data,
        step4: {
          ...prev.data.step4,
          additionalAnswers: answers,
          progress: {
            answered: answeredCount,
            total: additionalQuestions.length,
          },
        },
      },
    })); // No { immediate: true } = uses 500ms debounce
  }, [additionalQuestions.length, updateProgress]);

  const handleAIQuestionRegenerated = useCallback((questionId: string, newQuestion: Question) => {
    console.log('Parent: Question regenerated', questionId, newQuestion.text);
    setAiQuestions(prev => {
      const updated = prev.map(q => q.id === questionId ? newQuestion : q);
      console.log('Parent: Updated questions', updated.map(q => ({ id: q.id, text: q.text })));
      
      // 🚀 IMMEDIATE SAVE - Question Regeneration
      // Saves instantly when user regenerates a question
      updateProgress((progressPrev) => ({
        ...progressPrev,
        data: {
          ...progressPrev.data,
          step3: {
            ...progressPrev.data.step3,
            aiQuestions: updated,
          },
          step4: {
            ...progressPrev.data.step4,
            questionRegenerations: {
              ...progressPrev.data.step4.questionRegenerations,
              [questionId]: (progressPrev.data.step4.questionRegenerations[questionId] || 0) + 1,
            },
          },
        },
      }), { immediate: true }); // ← IMMEDIATE SAVE
      
      return updated;
    });
  }, [updateProgress]);


  const handleOverviewContinue = () => {
    if (!selectedIndustry || !selectedRole || !questionSet) {
      return;
    }

    if (shouldShowAdditionalStep) {
      setCurrentStep('additional-questions');
      updateProgress((prev) => ({
        ...prev,
        currentStep: 'additional-questions',
      }), { immediate: true });
    } else {
      setCurrentStep('document-upload');
      updateProgress((prev) => ({
        ...prev,
        currentStep: 'document-upload',
      }), { immediate: true });
    }
  };

  const handleAdditionalStepContinue = useCallback(() => {
    setCurrentStep('document-upload');
    updateProgress((prev) => ({
      ...prev,
      currentStep: 'document-upload',
      data: {
        ...prev.data,
        step4: {
          ...prev.data.step4,
          completedAt: new Date().toISOString(),
        },
      },
    }), { immediate: true });
  }, [updateProgress]);

  const handleDocumentUploaded = useCallback(async (document: UploadedDocumentSummary) => {
    setUploadedDocuments((prev) => {
      const filtered = prev.filter((item) => item.id !== document.id);
      const updated = [...filtered, document];
      
      updateProgress((progressPrev) => ({
        ...progressPrev,
        data: {
          ...progressPrev.data,
          step5: {
            documents: updated,
            lastUploadedAt: document.uploadedAt,
          },
        },
      }), { immediate: true });
      
      return updated;
    });
    setDocumentUploadError(null);

    // If we don't have assessmentId yet, try to get it after document upload
    if (!assessmentId && isSignedIn) {
      try {
        const token = await getToken();
        if (token) {
          const response = await axios.get(`${getApiUrl()}/assessments/progress`, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
          
          console.log('🔍 Assessment progress after document upload:', {
            hasData: !!response.data,
            dataKeys: response.data ? Object.keys(response.data) : [],
            id: response.data?.id,
            _id: response.data?._id
          });
          
          if (response.data) {
            const assessmentIdValue = response.data.id || response.data._id;
            if (assessmentIdValue) {
              setAssessmentId(assessmentIdValue);
              console.log('✅ Assessment ID fetched after document upload:', assessmentIdValue);
            } else {
              console.warn('⚠️ Assessment data exists but no ID found after document upload');
            }
          }
        }
      } catch (err: any) {
        console.error('Error fetching assessment ID after document upload:', err);
      }
    }
  }, [updateProgress, assessmentId, isSignedIn, getToken]);

  const handleDocumentRemoved = useCallback(async (documentId: string) => {
    // Update local state immediately for better UX
    setUploadedDocuments((prev) => {
      const updated = prev.filter((doc) => doc.id !== documentId);
      const lastUploadedAt = updated.length > 0 ? updated[updated.length - 1].uploadedAt : null;
      
      updateProgress((progressPrev) => ({
        ...progressPrev,
        data: {
          ...progressPrev.data,
          step5: {
            documents: updated,
            lastUploadedAt,
          },
        },
      }), { immediate: true });
      
      return updated;
    });
    
    // Try to delete from backend (but don't block UI if it fails)
    // Silently handle errors - document is already removed from UI
    try {
      await apiService.deleteDocument(documentId);
      console.log(`Document ${documentId} deleted successfully from backend`);
      setDocumentUploadError(null);
    } catch (error: any) {
      // Log error for debugging but don't show to user
      // Document is already removed from UI, so user experience is not affected
      console.log(`Document deletion from backend:`, {
        documentId,
        status: error.status,
        message: error.message,
        note: error.status === 404 ? 'Document not found (already removed or never existed)' : 'Other error'
      });
      
      // Only show error for critical issues (not 404, which is expected if document doesn't exist)
      if (error.status && error.status !== 404) {
        // Only show warning for server errors (500+) or network issues
        if (error.status >= 500 || error.status === 0) {
          setDocumentUploadError(
            `Note: Document removed from list. ${error.status === 0 ? 'Could not verify deletion with server.' : 'Server error occurred.'}`
          );
        }
        // For 400-level errors other than 404, silently ignore (document already removed from UI)
      }
      // For 404 or no status, silently ignore (document already removed from UI)
    }
  }, [updateProgress, uploadedDocuments]);

  const handleCompleteAssessment = useCallback(async () => {
    if (!selectedIndustry || !selectedRole || !questionSet || isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    try {
      const token = await getToken();
      if (!token) {
        console.error('No authentication token available');
        setIsSubmitting(false);
        return;
      }

      let finalAssessmentId = assessmentId;

      // If we don't have assessmentId, try to get it or create it
      if (!finalAssessmentId) {
        console.log('📝 No assessmentId found, fetching or creating one...');
        
        // First, try to get existing assessment
        try {
          const progressResponse = await axios.get(`${getApiUrl()}/assessments/progress`, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
          
          if (progressResponse.data) {
            finalAssessmentId = progressResponse.data.id || progressResponse.data._id;
            console.log('✅ Assessment ID found:', finalAssessmentId);
            setAssessmentId(finalAssessmentId);
          }
        } catch (err) {
          console.log('No existing assessment found, will create one');
        }
        
        // If still no ID, create assessment directly in backend by syncing progress
        if (!finalAssessmentId) {
          console.log('📤 Creating assessment in backend by syncing progress...');
          
          // Prepare progress data for backend
          const backendProgress = {
            session_id: progress.sessionId,
            current_step: progress.currentStep,
            company_name: progress.data.step1.companyName || '',
            industry_id: progress.data.step1.industry?.id || null,
            industry_name: progress.data.step1.industry?.name || null,
            industry_completed_at: progress.data.step1.completedAt || null,
            role_id: progress.data.step2.role?.id || null,
            role_name: progress.data.step2.role?.name || null,
            role_completed_at: progress.data.step2.completedAt || null,
            question_set: progress.data.step3.questionSet || null,
            ai_questions: progress.data.step3.aiQuestions || null,
            ai_answers: progress.data.step3.aiAnswers || {},
            ai_questions_generated_at: progress.data.step3.reviewedAt || null,
            additional_questions: progress.data.step4.additionalQuestions || null,
            additional_answers: progress.data.step4.additionalAnswers || {},
            additional_questions_generated_at: progress.data.step4.completedAt || null,
            question_regenerations: progress.data.step4.questionRegenerations || {},
            skipped_questions: progress.data.step4.skippedQuestions || [],
            last_saved: progress.lastSaved,
            expires_at: progress.expiresAt
          };
          
          try {
            // POST to create/update assessment - this returns the Assessment with ID
            const createResponse = await axios.post(
              `${getApiUrl()}/assessments/progress`,
              backendProgress,
              {
                headers: {
                  Authorization: `Bearer ${token}`
                }
              }
            );
            
            if (createResponse.data) {
              finalAssessmentId = createResponse.data.id || createResponse.data._id;
              console.log('✅ Assessment created/updated, ID obtained:', finalAssessmentId);
              setAssessmentId(finalAssessmentId);
            } else {
              console.warn('⚠️ Assessment created but no ID in response');
            }
          } catch (err: any) {
            console.error('❌ Error creating assessment in backend:', err);
            setError('Error al crear el assessment en el servidor. Por favor intenta de nuevo.');
            setIsSubmitting(false);
            return;
          }
        }
      }

      // If we still don't have an ID, we can't complete
      if (!finalAssessmentId) {
        console.error('❌ Cannot complete assessment: No assessment ID available');
        setError('No se pudo obtener el ID del assessment. Por favor intenta de nuevo.');
        setIsSubmitting(false);
        return;
      }

      // Call the backend complete endpoint
      console.log('🚀 Completing assessment:', finalAssessmentId);
      const response = await axios.post(
        `${getApiUrl()}/assessments/${finalAssessmentId}/complete`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log('✅ Assessment completed successfully:', response.data);

      // Clear local progress
      clearAllProgress();

      // Check if backend returned a redirect URL
      if (response.data?.redirect) {
        navigate(response.data.redirect);
      } else {
        // Navigate to thank-you page
        navigate('/thank-you');
      }
    } catch (error: any) {
      console.error('❌ Error completing assessment:', error);
      setError(
        error.response?.data?.detail || 
        'Error al completar el assessment. Por favor intenta de nuevo.'
      );
      // Still navigate to thank you page on error (user has completed the form)
      clearAllProgress();
      navigate('/thank-you');
    } finally {
      setIsSubmitting(false);
    }
  }, [assessmentId, clearAllProgress, getToken, navigate, onSetupComplete, questionSet, selectedIndustry, selectedRole, isSubmitting, saveNow]);

  const handleStartNewClick = () => {
    setShowStartNewConfirm(true);
  };

  const handleConfirmStartNew = () => {
    // Clear all progress and reset state
    clearAllProgress();
    setCompanyName('');
    setSelectedIndustry(null);
    setSelectedRole(null);
    setQuestionSet(null);
    setAiQuestions([]);
    setAiAnswers({});
    setAdditionalQuestions([]);
    setAdditionalAnswers({});
    setShowAdditionalQuestions(false);
    setUploadedDocuments([]);
    setDocumentUploadError(null);
    setCurrentStep('info');
    setShowStartNewConfirm(false);
    setError(null);
  };

  const handleCancelStartNew = () => {
    setShowStartNewConfirm(false);
  };

  const handleInfoContinue = () => {
    if (!companyName.trim() || !selectedIndustry || !selectedRole) {
      setError('Por favor completa todos los campos para continuar');
      return;
    }
    
    handleRoleSelect(selectedRole);
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 'info':
        return (
          <div className={styles.overview}>
            <div className={styles.overviewHeader}>
              <h2>Información de tu Empresa</h2>
            </div>
            
            {/* Company Name Input */}
            <div style={{ marginBottom: '2.5rem', maxWidth: '500px', margin: '0 auto 2.5rem' }}>
              <label 
                htmlFor="companyName" 
                style={{ 
                  display: 'block',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  color: 'var(--text-muted, #94a3b8)',
                  marginBottom: '0.75rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}
              >
                Nombre de tu Empresa
              </label>
              <input
                type="text"
                id="companyName"
                value={companyName}
                onChange={(e) => handleCompanyNameChange(e.target.value)}
                placeholder="Ej: Acme Corporation"
                required
                style={{
                  width: '100%',
                  padding: '1rem 1.5rem',
                  fontSize: '1.125rem',
                  borderRadius: '16px',
                  border: '1.5px solid rgba(0, 0, 0, 0.08)',
                  background: 'rgba(255, 255, 255, 0.8)',
                  backdropFilter: 'blur(16px)',
                  WebkitBackdropFilter: 'blur(16px)',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.03)',
                  color: 'var(--text-primary, #1a1a2e)'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = 'rgba(59, 130, 246, 0.5)';
                  e.target.style.background = 'rgba(255, 255, 255, 0.95)';
                  e.target.style.boxShadow = '0 0 0 4px rgba(59, 130, 246, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgba(0, 0, 0, 0.08)';
                  e.target.style.background = 'rgba(255, 255, 255, 0.8)';
                  e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.03)';
                }}
              />
            </div>

            {/* Industry Selection */}
            <IndustrySelector
              onIndustrySelect={handleIndustrySelect}
              selectedIndustry={selectedIndustry}
            />

            {/* Role Selection - Shows after industry is selected */}
            {selectedIndustry && (
              <div style={{ marginTop: '2rem', animation: 'slideUp 0.4s ease-out' }}>
                <RoleSelector
                  industry={selectedIndustry}
                  onRoleSelect={setSelectedRole}
                  onBack={() => {}}
                  selectedRole={selectedRole}
                />
              </div>
            )}

            <div className={styles.actions}>
              <button
                onClick={handleInfoContinue}
                className={styles.startButton}
                disabled={!companyName.trim() || !selectedIndustry || !selectedRole}
                style={{
                  '--theme-color': selectedIndustry?.themeColor || '#3B82F6'
                } as React.CSSProperties}
              >
                Continuar a Preguntas Iniciales
              </button>
              {(!companyName.trim() || !selectedIndustry || !selectedRole) && (
                <p className={styles.buttonHint}>
                  Por favor completa todos los campos para continuar
                </p>
              )}
            </div>
          </div>
        );
      
      case 'overview':
        return (
          <div className={styles.overview}>
            <div className={styles.overviewHeader}>
              <button
                onClick={handleBack}
                className={styles.backButton}
              >
                <i className="fas fa-arrow-left"></i>
                Volver a Información
              </button>
              <h2>Preguntas Iniciales</h2>
            </div>
            <div className={styles.selectionSummary}>
              <div className={styles.selectionItem}>
                <h3>Empresa</h3>
                <p>{companyName}</p>
              </div>
              <div className={styles.selectionItem}>
                <h3>Industria</h3>
                <p>{selectedIndustry?.name}</p>
                <span className={styles.selectionDescription}>
                  {selectedIndustry?.description}
                </span>
              </div>
              <div className={styles.selectionItem}>
                <h3>Rol</h3>
                <p>{selectedRole?.name}</p>
                <span className={styles.selectionDescription}>
                  {selectedRole?.description}
                </span>
              </div>
            </div>

            {/* AI Questions Section */}
            {selectedIndustry && selectedRole && aiQuestions.length > 0 && (
              <div className={styles.aiQuestionsSection}>
                <AIQuestions
                  questions={aiQuestions}
                  initialAnswers={aiAnswers} // ← CRITICAL: Pass saved answers to restore inputs
                  onAnswersChange={handleAIAnswersChange}
                  industryId={selectedIndustry.id}
                  roleId={selectedRole.id}
                  onQuestionRegenerated={handleAIQuestionRegenerated}
                />
              </div>
            )}

            <div className={styles.actions}>
              <button
                onClick={handleOverviewContinue}
                className={styles.startButton}
                disabled={!allAIQuestionsAnswered}
                style={{
                  '--theme-color': selectedIndustry?.themeColor || '#3B82F6'
                } as React.CSSProperties}
              >
                {shouldShowAdditionalStep ? 'Continuar a Preguntas Detalladas' : 'Ir a Documentos'}
              </button>
              {!allAIQuestionsAnswered && (
                <p className={styles.buttonHint}>
                  Por favor responde las {aiQuestions.length} preguntas iniciales para continuar
                </p>
              )}
            </div>
          </div>
        );

      case 'additional-questions':
        return (
          <div className={styles.additionalQuestions}>
            <div className={styles.overviewHeader}>
              <button
                onClick={() => setCurrentStep('overview')}
                className={styles.backButton}
              >
                <i className="fas fa-arrow-left"></i>
                Volver a Preguntas Iniciales
              </button>
              <h2>Preguntas Detalladas</h2>
            </div>

            {selectedIndustry && selectedRole && (
              <AdditionalQuestions
                industryId={selectedIndustry.id}
                roleId={selectedRole.id}
                initialResponses={aiQuestions.map(q => ({
                  questionId: q.id,
                  questionText: q.text,
                  answer: aiAnswers[q.id] || '',
                  category: q.category
                }))}
                savedQuestions={additionalQuestions} // ← CRITICAL: Pass saved questions to avoid regeneration
                savedAnswers={additionalAnswers} // ← CRITICAL: Pass saved answers to restore inputs
                onQuestionsGenerated={handleAdditionalQuestionsGenerated}
                onAnswersChange={handleAdditionalAnswersChange}
                shouldGenerate={currentStep === 'additional-questions'}
              />
            )}

            <div className={styles.actions}>
              <button
                onClick={handleAdditionalStepContinue}
                className={styles.startButton}
                style={{
                  '--theme-color': selectedIndustry?.themeColor || '#3B82F6'
                } as React.CSSProperties}
              >
                Continuar a Documentos
              </button>
            </div>
          </div>
        );
      
      case 'document-upload':
        try {
          return (
            <div className={styles.documentUpload}>
              <div className={styles.overviewHeader}>
                <button
                  onClick={() => setCurrentStep(shouldShowAdditionalStep ? 'additional-questions' : 'overview')}
                  className={styles.backButton}
                >
                  <i className="fas fa-arrow-left"></i>
                  Volver
                </button>
                <h2>Documentos de Soporte</h2>
              </div>
              <p className={styles.documentUploadIntro}>
                Comparte archivos TXT, MD o PDF (máx. 10MB) con contexto adicional para acelerar el diagnóstico.
              </p>
              <DocumentUpload
                documents={uploadedDocuments}
                contextId={assessmentId || progress.sessionId}
                onDocumentUploaded={handleDocumentUploaded}
                onDocumentRemoved={handleDocumentRemoved}
                onError={(message) => setDocumentUploadError(message)}
                maxFileSizeMB={10}
              />
              <p className={styles.documentUploadHint}>
                Este paso es opcional, pero los documentos ayudan a proporcionar recomendaciones más precisas.
              </p>
              {documentUploadError && (
                <div className={styles.error}>
                  <p>{documentUploadError}</p>
                </div>
              )}
              <div className={styles.actions} style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '2rem' }}>
                <div style={{ textAlign: 'center' }}>
                  <p style={{ marginBottom: '1rem', color: '#666', fontSize: '0.9rem' }}>
                    Una vez que hayas subido tus documentos (o si prefieres omitir este paso), haz clic en el botón de abajo para completar tu assessment.
                    Tu reporte se generará automáticamente y nuestro equipo lo revisará.
                  </p>
                </div>
                <div style={{ textAlign: 'center', paddingTop: '1rem', borderTop: '1px solid #e0e0e0' }}>
                  <button
                    onClick={handleCompleteAssessment}
                    disabled={isSubmitting}
                    className={styles.completeButton}
                    style={{
                      '--theme-color': selectedIndustry?.themeColor || '#0ea5e9',
                    } as React.CSSProperties}
                  >
                    {isSubmitting ? (
                      <>
                        <span className={styles.spinner} style={{ width: '1rem', height: '1rem', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: 'white', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }}></span>
                        Completando...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-check-circle"></i>
                        Completar Assessment
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          );
        } catch (err) {
          console.error('Error rendering document-upload step:', err);
          return (
            <div className={styles.error}>
              <p>Ocurrió un error al cargar este paso. Por favor recarga la página.</p>
              <button onClick={() => window.location.reload()}>Recargar Página</button>
            </div>
          );
        }
      
      default:
        return null;
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerTop}>
          <div>
        <h1>Evaluación de Procesos</h1>
        <p>Cuéntanos sobre tu negocio para personalizar tu evaluación</p>
          </div>
          <div className={styles.headerActions}>
            <SaveIndicator saveStatus={saveStatus} />
            {(selectedIndustry || companyName) && (
              <button 
                onClick={handleStartNewClick} 
                className={styles.startNewButton}
                title="Iniciar nueva evaluación"
              >
                <i className="fas fa-redo"></i>
                Nuevo
              </button>
            )}
          </div>
        </div>
      </div>

      {showStartNewConfirm && (
        <div className={styles.confirmOverlay}>
          <div className={styles.confirmModal}>
            <h3>
              <i className="fas fa-exclamation-triangle"></i>
              ¿Iniciar Nueva Evaluación?
            </h3>
            <p>Esto eliminará todo tu progreso actual. ¿Estás seguro?</p>
            <div className={styles.confirmActions}>
              <button onClick={handleCancelStartNew} className={styles.cancelButton}>
                Cancelar
              </button>
              <button onClick={handleConfirmStartNew} className={styles.confirmButton}>
                Sí, Iniciar Nueva
              </button>
            </div>
          </div>
        </div>
      )}

      <ProgressIndicator
        steps={steps}
        currentStep={steps.findIndex(step => step.current)}
        themeColor={selectedIndustry?.themeColor}
      />

      {error && (
        <div className={styles.error}>
          <p>{error}</p>
        </div>
      )}

      {loading && (
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <p>Cargando preguntas...</p>
        </div>
      )}

      {!loading && (
        <div className={styles.stepContent}>
          {renderCurrentStep()}
        </div>
      )}
    </div>
  );
};

export default AssessmentSetup;
